import type { Property } from '@protector/entities/property/models';
import type { Crop } from '@protector/utils/types';
import { useWindowResize } from 'beautiful-react-hooks';
import 'core/components/menu/menu.styles.less';
import { getCurrentWorkspace, getInterfaceLanguage, getLicensingStatus } from 'core/core.selectors';
import { selectCurrentUser } from 'core/services/auth/auth.reducer';
import { handleInterfaceLanguage } from 'core/utils/functions';
import useSegmentTracking from 'core/utils/segment/useSegmentTracking';
import { getSelectedCompany } from 'entities/company/company.selectors';
import { getSelectedProperty } from 'entities/property/property.selectors';
import { isActive } from 'entities/season/season.functions';
import { useGetWorkspace } from 'querys/workspace/workspace.query';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { getPlanInfo } from './events.utils';

export { getPlanInfo };

export const getCropInfo = (farm: Property | undefined, key: keyof Crop) => {
  const cropInfo = new Set();

  return (
    farm?.seasons.reduce<Crop[keyof Crop][]>((acc, season) => {
      if (cropInfo.has(season.crop[key])) {
        return acc;
      }

      if (isActive(season)) {
        cropInfo.add(season.crop[key]);
        acc.push(season.crop[key]);

        return acc;
      }

      return acc;
    }, []) ?? null
  );
};

type Values = Record<string, unknown>;

const useIdentifyUser = () => {
  const { reset, identify } = useSegmentTracking();

  const user = useSelector(selectCurrentUser);
  const interfaceLanguage = useSelector(getInterfaceLanguage);
  const selectedCompany = useSelector(getSelectedCompany);
  const selectedProperty = useSelector(getSelectedProperty);
  const licensingStatus = useSelector(getLicensingStatus);
  const currentWorkspace = useSelector(getCurrentWorkspace);
  const { data: workspaceInfo } = useGetWorkspace(currentWorkspace?.id);

  const [resolutionScreen, setResolutionScreen] = useState(`${window.innerWidth}x${window.innerHeight}`);
  useWindowResize(() => {
    setResolutionScreen(`${window.innerWidth}x${window.innerHeight}`);
  });

  useEffect(() => {
    const values: Values = {
      userId: null,
      jobTitle: null,
      userLanguage: null,
      interfaceLanguage: handleInterfaceLanguage(interfaceLanguage),
      asset: 'Web Panel',
      organizationId: null,
      organizationName: null,
      farmId: null,
      farmName: null,
      workspaceId: null,
      workspaceName: null,
      workspaceType: null,

      planId: getPlanInfo(licensingStatus, 'id'),
      planName: getPlanInfo(licensingStatus, 'name'),
      cropId: getCropInfo(selectedProperty, 'id'),
      cropName: getCropInfo(selectedProperty, 'name'),
      resolutionScreen
    };

    if (user) {
      values.userId = user.id;
      values.userLanguage = user.locale;
      values.jobTitle = user.job_title;
    }

    if (selectedCompany) {
      values.organizationId = selectedCompany.id;
      values.organizationName = selectedCompany.name;
    }

    if (selectedProperty) {
      values.farmId = selectedProperty.id;
      values.farmName = selectedProperty.name;
    }

    if (workspaceInfo) {
      values.workspaceId = workspaceInfo.data.id;
      values.workspaceName = workspaceInfo.data.name;
      values.workspaceType = workspaceInfo.data.use_type;
    }
    if (values.userId) {
      reset();
      identify(values.userId as string, values);
    }
  }, [user, selectedCompany, workspaceInfo, selectedProperty, licensingStatus, interfaceLanguage, identify, reset, resolutionScreen]);
};

export default useIdentifyUser;
